import format from 'date-fns/format'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { Description, MainHeadline, SmallHeadline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { ScheduledItemCalendarContainer } from 'components/Calendar'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getRole, getStoreRef } from 'reducks/auth/selectors'
import { fetchClosedDates } from 'reducks/calendar/operations'
import { endOfMonth, endOfToday, isAfter, isSameDay, startOfMonth } from 'date-fns'
import { getHolidays, getLastOrderTime, getOrderStartTime, getStoreName } from 'reducks/store/selectors'
import { SearchForm, SelectBox } from 'components/Form'
import { fetchBreads } from 'reducks/bread/operations'
import { BreadType } from 'types/bread'
import { getBreads } from 'reducks/bread/selectors'
import { getCategories } from 'reducks/category/selectors'
import {
  useHandleCheckedOrderTypes,
  useHandleKeyword,
  useInitBreadList,
  useSearchBreads,
  useSelectCategory,
} from 'hooks/bread'
import { fetchEnabledCategories } from 'reducks/category/operations'
import { fetchStocks, initStocks, unsubscribeStocks } from 'reducks/stock/operations'
import { dateToString, parseTimeToDate } from 'lib/helper'
import { getStocks } from 'reducks/stock/selectors'
import { FilterLabel, OrderTypeCheckboxes } from 'components/Order'
import { FlexRow } from 'components/Flex'
import { CautionCard, LargeMenuCard, ScheduledItemCardList } from 'components/Card'
import { Tabs } from 'components/Tab'
import { fetchSchedules } from 'reducks/schedule/operations'
import { getSchedules } from 'reducks/schedule/selectors'
import { Role } from 'lib/role'
import { NavButton } from 'components/Button'
import { fetchOrders, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { getClosedDates, getSpecialHolidays } from 'reducks/calendar/selectors'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { DaysOfWeek } from 'lib/daysOfWeek'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { getOrders } from 'reducks/checkout/selectors'
import { useEarliestBusinessTime, useLatestOrderStartTime } from 'hooks/businessHour'
import { useIsAfterLastOrder, useNextBusinessDate } from 'hooks/store'
import { useGetTemporaryOpen } from 'hooks/storeCalendar'
import { useCategoryOptions } from 'hooks/category'
import { useAdjustStocks } from 'hooks/stock'
import { useCheckoutItemsFromOrders } from 'hooks/checkout'
import { StockUnionType } from 'types/stock'
import { BusinessTimeType } from 'types/store'
const daysOfWeek = new DaysOfWeek()

type Props = {
  date: Date
  title: string
}

const ScheduledItemTemplate: FC<Props> = (props) => {
  const { date, title } = props

  const dispatch = useDispatch()

  const breads = useSelector((state: State) => getBreads(state))
  const categories = useSelector((state: State) => getCategories(state))
  const closedDates = useSelector((state: State) => getClosedDates(state))
  const holidays = useSelector((state: State) => getHolidays(state))
  const orders = useSelector((state: State) => getOrders(state))
  const schedules = useSelector((state: State) => getSchedules(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const stocks = useSelector((state: State) => getStocks(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const storeName = useSelector((state: State) => getStoreName(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const lastOrderTime: BusinessTimeType = useSelector((state: State) => getLastOrderTime(state))
  const orderStartTime: BusinessTimeType = useSelector((state: State) => getOrderStartTime(state))
  const role = useSelector((state: State) => getRole(state))
  const roleManager = new Role(role, storeRef)
  const query = useSelector((state: State) => state.router.location.query)
  const currentTab = (query.tab as 'released' | 'unreleased') ?? 'released'
  const sortKey = 'order'

  const [currentMonth, setCurrentMonth] = useState<Date>(date)
  const [categoryName, setCategoryName] = useState<string>('')
  const [keyword, setKeyword] = useState<string>('')
  const [breadList, setBreadList] = useState<BreadType[]>([])
  const [checkedOrderTypes, setCheckedOrderTypes] = useState<string[]>(['daily', 'preorder'])

  const checkoutItems = useCheckoutItemsFromOrders(orders)
  const categoryOptions = useCategoryOptions(categories)
  const orderTypes = ['daily', 'preorder']
  const isFuture = isAfter(date, endOfToday())
  const now = new Date()
  const dayOfToday = daysOfWeek.getDayOfWeek(now, specialHolidays)
  const nextBusinessDate = useNextBusinessDate(closedDates)
  const temporaryOpen = useGetTemporaryOpen(dateToString(date), storeCalendars)
  const dayOfWeek = daysOfWeek.getDayOfWeek(date, specialHolidays)
  // 臨時営業日の引き継ぎ曜日が設定されている場合は優先する
  const defaultDayOfWeek = temporaryOpen?.default_day_of_week || dayOfWeek
  const latestOrderStartTime = useLatestOrderStartTime([dayOfWeek], orderStartTime, temporaryOpen)
  const earliestLastOrderTime = useEarliestBusinessTime([dayOfToday], lastOrderTime, 'last_order_time', temporaryOpen)
  const lastOrderDateTime = parseTimeToDate(earliestLastOrderTime)
  const isAfterLastOrder = useIsAfterLastOrder(now, lastOrderDateTime)
  const isTemporaryClose = useMemo(() => {
    return storeCalendars.some((storeCalendar) => {
      return storeCalendar.status === 'close' && storeCalendar.date === dateToString(date)
    })
  }, [storeCalendars])
  const isClosed = closedDates.some((closedDate) => isSameDay(closedDate, date))
  const strDate = format(isAfterLastOrder || isClosed ? nextBusinessDate : date, 'yyyyMMdd')
  const dailySchedules = useMemo(() => schedules.filter((schedule) => schedule.type === 'daily'), [schedules])
  const preorderSchedules = useMemo(() => schedules.filter((schedule) => schedule.type === 'preorder'), [schedules])

  const handleCurrentMonth = useCallback((month: Date) => setCurrentMonth(month), [setCurrentMonth])

  // 販売予定と未販売のタブ切り替え
  const tabs = useMemo(
    () => [
      {
        isSelected: currentTab === 'released',
        label: '販売予定',
        url: `/scheduled_item/${dateToString(date)}?tab=released`,
      },
      {
        isSelected: currentTab === 'unreleased',
        label: '未販売',
        url: `/scheduled_item/${dateToString(date)}?tab=unreleased`,
      },
    ],
    [currentTab, date]
  )

  // 注文された後に在庫データが削除されてしまった場合（有効在庫数がマイナスの場合）に在庫データを生成する
  const stockList = useAdjustStocks(
    breads,
    checkoutItems,
    strDate,
    latestOrderStartTime,
    earliestLastOrderTime,
    storeName,
    schedules,
    stocks,
    storeRef,
    checkedOrderTypes
  )
  // 選択されたカテゴリーに応じてフィルタリング
  const selectCategoryName = useSelectCategory({
    breads,
    categories,
    keyword,
    setBreadList,
    setCategoryName,
    isShownPrivate: false,
    stocks: stockList,
    schedules,
    isFuture,
    checkedOrderTypes,
    dayOfWeek: defaultDayOfWeek,
    isTemporaryClose,
    currentTab,
  })
  // 検索キーワードの入力
  const handleKeyword = useHandleKeyword({
    breads,
    categories,
    categoryName,
    setBreadList,
    setKeyword,
    isShownPrivate: false,
    stocks: stockList,
    schedules,
    isFuture,
    checkedOrderTypes,
    dayOfWeek: defaultDayOfWeek,
    isTemporaryClose,
    currentTab,
  })
  // 商品タイプの選択
  const handleCheckedOrderTypes = useHandleCheckedOrderTypes({
    breads,
    categories,
    categoryName,
    setBreadList,
    setCheckedOrderTypes,
    keyword,
    isShownPrivate: false,
    stocks: stockList,
    schedules,
    isFuture,
    dayOfWeek: defaultDayOfWeek,
    isTemporaryClose,
    currentTab,
  })
  // キーワード検索(部分一致)
  const searchBreads = useSearchBreads({
    breads,
    categories,
    categoryName,
    setBreadList,
    isShownPrivate: false,
    stocks: stockList,
    schedules,
    isFuture,
    checkedOrderTypes,
    dayOfWeek: defaultDayOfWeek,
    isTemporaryClose,
    currentTab,
  })
  // Reduxで取得したstocksをローカルのbreadListに反映する
  useInitBreadList({
    breads,
    categories,
    categoryName,
    keyword,
    setBreadList,
    isShownPrivate: false,
    stocks: stockList,
    schedules,
    isFuture,
    checkedOrderTypes,
    dayOfWeek: defaultDayOfWeek,
    isTemporaryClose,
    currentTab,
  })

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'all', sortKey))
  }, [storeRef, sortKey])

  // カテゴリーを取得
  useEffect(() => {
    dispatch(fetchEnabledCategories())
  }, [])

  // 月が変わったら休業日を取得する
  useEffect(() => {
    if (!storeRef) return
    dispatch(
      fetchClosedDates(startOfMonth(currentMonth), endOfMonth(currentMonth), holidays, specialHolidays, storeRef)
    )
  }, [currentMonth, holidays, storeRef, specialHolidays])

  useEffect(() => {
    if (!storeRef) return
    dispatch(initStocks())
    dispatch(
      fetchStocks({ date: dateToString(date), types: checkedOrderTypes, storeRef, isExcludingDeletedStock: false })
    )
    return () => {
      dispatch(unsubscribeStocks())
    }
  }, [storeRef, checkedOrderTypes, date])

  useEffect(() => {
    if (!storeRef) return
    dispatch(initOrders())
    dispatch(
      fetchOrders(
        dateToString(date),
        'すべて',
        checkedOrderTypes as StockUnionType[],
        ['requires_capture', 'succeeded'],
        storeRef
      )
    )
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [storeRef, date, checkedOrderTypes])

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchSchedules(storeRef, ['daily', 'preorder']))
  }, [storeRef])

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(date)))
  }, [storeRef, date])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={title} />
      <Spacer size={20} />
      <Description
        description={
          '確認したい日付を選択して、販売予定の商品を確認してください。お店の休業日や、販売予定の商品が無い日はグレーで表示されています。'
        }
      />
      <Spacer size={20} />
      <ScheduledItemCalendarContainer
        currentDate={date}
        currentMonth={currentMonth}
        handleCurrentMonth={handleCurrentMonth}
      />
      <Spacer size={40} />
      <Tabs tabs={tabs} />
      <Spacer size={73} />
      <SearchForm
        isFilled={keyword !== ''}
        onChange={handleKeyword}
        onClick={() => searchBreads(keyword)}
        placeholder={'商品の名前で検索する'}
        value={keyword}
      />
      <Spacer size={12} />
      <SelectBox options={categoryOptions} select={selectCategoryName} value={categoryName} />
      <Spacer size={12} />
      <FilterLabel label={'商品タイプ'} />
      <Spacer size={8} />
      <OrderTypeCheckboxes
        checkedOrderTypes={checkedOrderTypes}
        orderTypes={orderTypes}
        handleCheckbox={handleCheckedOrderTypes}
      />
      <Spacer size={20} />
      <FlexRow justifyContent={'start'}>
        <SmallHeadline label={`${breadList.length}件の商品を表示中`} />
      </FlexRow>
      <Spacer size={12} />
      {breadList.length > 0 ? (
        <ScheduledItemCardList
          breads={breadList}
          checkedOrderTypes={checkedOrderTypes}
          currentTab={currentTab}
          date={date}
          dayOfWeek={defaultDayOfWeek}
          isAfterLastOrder={isAfterLastOrder}
          isFuture={isFuture}
          isTemporaryClose={isTemporaryClose}
          dailySchedules={dailySchedules}
          preorderSchedules={preorderSchedules}
          stocks={stockList}
        />
      ) : (
        <CautionCard caution={'販売が予定されている商品がありません'} description={''} />
      )}
      <Spacer size={40} />
      {roleManager.hasEnoughRole(9) && (
        <>
          <LargeMenuCard
            description={
              '当日取り置きできる商品の在庫を増減したり、お客さまに商品が取り置きできる通知を送ることが出来ます。'
            }
            iconPath={'/icon/blue/stock.svg'}
            url={'/stock'}
            title={'在庫の増減と通知'}
          />
          <Spacer size={20} />
          <LargeMenuCard
            description={'商品別に事前予約を受け付ける日を設定することができます。'}
            iconPath={'/icon/blue/reservation.svg'}
            url={'/schedule'}
            title={'事前予約日設定'}
          />
        </>
      )}
      <Spacer size={48} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={48} />
    </div>
  )
}

export default ScheduledItemTemplate
